// @ts-strict-ignore
import {Component, Input, OnChanges, OnInit, output} from "@angular/core";
import {AddTagToTaggableRequest, AddTagToTaggableResponse, ITaggable, RemoveTagFromTaggableRequest, Tag, TagType} from "@models";
import {FormArray, FormBuilder, FormControl, FormGroup} from "@angular/forms";
import {Translations} from "@translations";
import {SimMaterialDialogService, SimTaggingService} from "@services";
import {VMAutoCompleteNormal} from "../sim-base-chip-input/sim-base-chip-input.component";

// TODO: Component needs a rework

/**
 * Component for displaying and editing tags associated with a taggable object.
 */
@Component({
	selector: "sim-tags-chip-input",
	templateUrl: "./sim-tags-chip-input.component.html",
	styleUrls: ["./sim-tags-chip-input.component.scss"],
	standalone: false,
})
export class SimTagsChipInputComponent implements OnInit, OnChanges {
	T = Translations;

	@Input() isEditMode: boolean = true;
	@Input() taggableObject: ITaggable;
	@Input({required: true}) tagType: TagType;
	@Input() placeholder: string;
	@Input() label: string;
	@Input() disabled: boolean;

	optionSelected = output<Tag>();
	optionCleared = output<Tag>();
	onChange = output<string[]>();

	autoCompleteData: VMAutoCompleteNormal<Tag>[] = [];

	currentTags: Tag[] = [];
	allTags: Tag[] = [];

	form: FormGroup;
	formState: FormArray<FormControl<Tag>>;

	constructor(
		private fb: FormBuilder,
		private _simTaggingService: SimTaggingService,
		private _dialogService: SimMaterialDialogService,
	) {}

	ngOnInit(): void {
		this.formState = this.fb.array([]) as FormArray;
		this.initTags();
	}

	ngOnChanges(): void {
		this.initTags();
	}

	reloadAutoCompleteData(searchString: string) {
		if (this.allTags) {
			this.autoCompleteData = this.filter(searchString);
		}
	}

	optionSelectedAddTagToTaggable(tag: Tag) {
		if (!!this.taggableObject) {
			this.addTagToTaggable(tag.name).then((response) => {
				this.reloadAutoCompleteData("");
				this.onChange.emit(response.taggableObject.tagIds);
			});
		} else {
			this.currentTags.push(tag);
			this.onChange.emit(this.currentTags.map((tag) => tag.id));
		}
	}

	async openCreateNewTagDialog(): Promise<void> {
		// open dialog
		const result = await this._dialogService.openCreateByNameDialog({title: "Create New Tag"});
		if (!result || result.action !== "ok") {
			return;
		}

		// create tag using dialog result
		const createResponse = await this._simTaggingService.createTag({name: result.newName, type: this.tagType});
		if (!!this.taggableObject) {
			const addResponse = await this.addTagToTaggable(createResponse.name);
			this.formState.push(new FormControl(addResponse.addedTag));
			this.reloadAutoCompleteData("");
			this.onChange.emit(addResponse.taggableObject.tagIds);
		} else {
			this.currentTags.push(createResponse);
			this.formState.push(new FormControl(createResponse));
			this.onChange.emit(this.currentTags.map((tag) => tag.id));
		}
	}

	optionClearedRemoveTag(tag: Tag) {
		if (!!this.taggableObject) {
			let taggableId = this.taggableObject.id;
			let removeTagFromTaggableRequest: RemoveTagFromTaggableRequest = {
				tagId: tag.id,
				taggableId: taggableId,
			};

			this._simTaggingService.removeTagFromTaggable(removeTagFromTaggableRequest).then((response) => {
				this.reloadAutoCompleteData("");
				this.onChange.emit(response.taggableObject.tagIds);
			});
		} else {
			let index = this.currentTags.indexOf(tag);
			if (index > -1) {
				this.currentTags.splice(index, 1);
			}
			this.onChange.emit(this.currentTags.map((tag) => tag.id));
		}
	}

	private initTags() {
		this.formState = this.fb.array([]) as FormArray;
		this._simTaggingService.getAllTagsOfTagType(this.tagType).then((tags) => {
			this.allTags = tags;
			this.reloadAutoCompleteData("");
			if (!!this.taggableObject) {
				this._simTaggingService.getTagsByIds(this.taggableObject.tagIds ?? []).then((tags) => {
					this.formState.clear();
					this.currentTags = [];
					tags.forEach((tag) => {
						this.formState.push(new FormControl(tag));
						this.currentTags.push(tag);
					});
					this.reloadAutoCompleteData("");
				});
			}
		});
	}

	private filter(searchString: string): VMAutoCompleteNormal<Tag>[] {
		return this.allTags
			.filter((tag) => tag.name.toLowerCase().includes(searchString.toLowerCase()))
			.map((tag) => {
				return {
					displayName: tag.name,
					value: tag,
				};
			});
	}

	private addTagToTaggable(tagName: string): Promise<AddTagToTaggableResponse> {
		let taggableId = this.taggableObject.id;
		let addToTaggableRequest: AddTagToTaggableRequest = {
			name: tagName,
			taggableId: taggableId,
			type: this.tagType,
		};
		return this._simTaggingService.addTagToTaggable(addToTaggableRequest);
	}
}
